import { autoinject, bindable, computedFrom, processContent } from 'aurelia-framework';
import { last, remove, map, flatten, filter, some } from 'lodash';
import { inheritBindingContext } from '../../component-model/template-elements';
import { NodeDraggingModel, NodeModel } from '../../models/node-model';
import { HtmlDrag, IDraggableHandler, IDroppableHandler } from '../drag-and-drop/models';
import { TreeView } from './tree-view';
import { NotificationType, ResourcedNotificationService } from '../notifications/notification-service';

@autoinject
@processContent(inheritBindingContext(true))
export class TreeNode extends HtmlDrag {
	@bindable current: NodeModel;

	@bindable draggableHandler: IDraggableHandler;
	@bindable droppableHandler: IDroppableHandler;

	// Set by the sub-view template placeholder
	@bindable hasSubView: boolean;

	@bindable isSelected: boolean;
	@bindable isExpanded: boolean;
	@bindable isSearchResult: boolean;
	@bindable isRecentlyChecked: boolean;
	@bindable isRecentlyUnChecked: boolean;
	@bindable useStickyNodeTitle: boolean;
	
	@bindable parent: TreeNode;
	@bindable tree: TreeView;
	
	selectedChildrenIndicatorVisible: boolean;
	selectedChildrenCount = 0;

	get currentTreeNode(): TreeNode {
		return this;
	}

	private childNodes: TreeNode[] = [];

	public nodeTitleElement: Element;
	private selectedCheckboxElement: HTMLInputElement;

	constructor(private element: Element, private notificationService: ResourcedNotificationService) {
		super();
	}

	set nodeTitle(value: Element) {
		this.nodeTitleElement = value;
	}

	@computedFrom('current')
	get currentNodeId(): string {
		return this.current.id;
	}

	@computedFrom('current')
	get currentNodeWrapperId(): string {
		return `wrapper-${this.current.id}`;
	}

	/**
	 * This is a workaround for https://github.com/aurelia/templating/issues/533
	 * Using a regular property as the target of a ref binding breaks when nested elements have
	 * identically named ref properties _and_ binding context inheritance is enabled.
	 */
	set selectedCheckbox(value: HTMLInputElement) {
		this.selectedCheckboxElement = value;
		this.selectedCheckboxElement.addEventListener('click', (ev: Event) => this.onSelectCheckboxClick(ev));
	}

	@computedFrom('current.children')
	get hasChildren(): boolean {
		return this.current.children.length > 0;
	}

	@computedFrom('current.parent')
	get isLastChild(): boolean {
		return this.current.parent && last(this.current.parent.children) === this.current;
	}

	@computedFrom('current.code')
	get hasCode(): boolean {
		return !!this.current.code;
	}

	@computedFrom('tree')
	get allowMultiSelect(): boolean {
		return this.tree.selectMode != "Single";
	}

	@computedFrom('hasChildren', 'hasSubView', 'isExpanded', 'useStickyNodeTitle')
	get useStickyTitle(): boolean {
		return this.isExpanded && this.useStickyNodeTitle && (this.hasChildren || this.hasSubView);
	}

	get isIndeterminate(): boolean {
		const hasIndeterminateChildren = some(this.childNodes, n => n.isIndeterminate);
		if (hasIndeterminateChildren) {
			return true;
		}

		const selectedChildren = filter(this.childNodes, n => n.isSelected).length;

		return selectedChildren > 0 && selectedChildren < this.childNodes.length;
	}

	isSelectedChanged(newValue: boolean, oldValue: boolean): void {

		if (newValue === oldValue) { return; }

		if (this.selectedCheckboxElement) {
			this.selectedCheckboxElement.checked = newValue;
		}

		if (this.parent) {
			this.parent.selectedChildrenChanged();
			this.parent.setSelectedChildrenCount(count => count + (newValue ? 1 : -1));
		}

		if (newValue === true) {
			this.isRecentlyUnChecked = false;
			this.isRecentlyChecked = true;
			setTimeout(() => this.isRecentlyChecked = false, 3500);
		}
		else {
			this.isRecentlyChecked = false;
			this.isRecentlyUnChecked = true;
			setTimeout(() => this.isRecentlyUnChecked = false, 3500);
		}
	}

	setSelectedChildrenCount(calc: (count: number) => number) {
		this.selectedChildrenCount = calc(this.selectedChildrenCount);

		if (this.parent) {
			this.parent.setSelectedChildrenCount(calc);
		}
	}

	updateIndeterminateStatus(): void {
		if (this.selectedCheckboxElement) {
			this.selectedCheckboxElement.indeterminate = this.isIndeterminate;
		}
	}

	selectedChildrenChanged(): void {
		if (this.tree.selectMode != "Multiple") {
			return;
		}

		this.isSelected = some(this.childNodes, n => n.isSelected);

		this.updateIndeterminateStatus();

		if (this.parent) {
			this.parent.selectedChildrenChanged();
		}
	}

	addChild(child: TreeNode): void {
		this.childNodes.push(child);
	}

	removeChild(child: TreeNode): void {
		remove(this.childNodes, n => n === child);
	}

	onClick(event: Event) {
		event.stopPropagation();
		if (this.tree.selectMode != "Single") {
			// Multiselect is handled via the checkbox event
			return;
		}

		if (this.isSelected) {
			// Unselect is only supported for multiselect trees
			return;
		}

		this.tree.selectNode(this);
	}

	// Check boxes are only shown in multiselect scenarios (not in TreeSelectMode.Single)
	onSelectCheckboxClick(event: Event) {
		const isSelecting = this.selectedCheckboxElement.checked;

		if (isSelecting) {
			if (this.tree.selectMode == "MultipleSeparately") {
				this.tree.selectNode(this);
			}
			else { // Multiple: we want to include all the parent and child nodes
				this.tree.selectNodes(this.getAncestors().concat(this.flattenSubtree()));
			}
		}

		// Unselecting:
		else {

			if (this.tree.selectMode == "MultipleSeparately") {
				this.tree.unselectNode(this);
			}
			else { // Multiple
				this.tree.unselectNodes(this.flattenSubtree()); // unselect this node and all descendants

				// When unselecting, only unselect the parent nodes that have no other 
				// selected children. After the call above, this node is considered unselected
				// by the tree, so any ancestor that has other selected children will be indeterminate.
				// Therefore we unselect everything _except_ the indeterminate ancestors.
				this.tree.unselectNodes(filter(this.getAncestors(), p => !p.isIndeterminate));
			}
		}
	}

	flattenSubtree(): TreeNode[] {
		return [this as TreeNode].concat(flatten(map(this.childNodes, n => n.flattenSubtree())));
	}

	getAncestors(): TreeNode[] {
		if (!this.parent) {
			return [];
		}

		return this.parent.getAncestors().concat([this.parent]);
	}

	toggleCollapse(event: Event) {
		event.stopPropagation();
		this.isExpanded = !this.isExpanded;
	}

	handleKeypress(e: KeyboardEvent) {
		e.stopPropagation();
		e.preventDefault();

		if (e.code == "Space" || e.code == "Enter") {
			if (!this.hasChildren) {
				this.isSelected ? 
					this.focusContent() :
					this.tree.selectNode(this);
			} else {
				this.isExpanded = !this.isExpanded;
			}
		}
	}

	focusContent() {
		const contentContainer = document.getElementById("contentContainer");
		if (!contentContainer) {
			return;
		}

		const contentToFocus = contentContainer.querySelector("input, summary, #contentToFocus");
		if (!contentToFocus || !(contentToFocus instanceof HTMLElement)) {
			return;
		}

		contentToFocus.focus();	
	}

	onDragStart(event): void {
		const model = new NodeDraggingModel(this.current);
		this.draggableHandler.onDragStart(event, model);
	}

	onToggleExpandDescendants(): void {
		this.childNodes.forEach((child: TreeNode) => child.isExpanded = this.isExpanded);
	}

	bind() {
		if (this.parent) {
			this.parent.addChild(this);

			this.tree = this.parent.tree;
		} else {
			this.tree.addRootNode(this);
		}

		if (this.tree.expandByDefault) {
			this.isExpanded = true;
		}

		this.selectedChildrenIndicatorVisible = this.tree.selectMode === 'MultipleSeparately';
	}

	attached() {
		this.initializeDom();

		if (this.tree.useStickyNodeTitle) {
			this.useStickyNodeTitle = true;
		}
	}

	unbind() {
		this.tree.nodeUnbound(this);

		if (this.parent) {
			this.parent.removeChild(this);
		} else {
			this.tree.removeRootNode(this);
		}
	}

	expandParents(): void {
		if (this.parent) {
			this.parent.isExpanded = true;
			this.parent.expandParents();
		}
	}

	/**
	 * Recursively toggles the expanded state for this node and all child nodes.
	 * 
	 * @param expand
	 */
	toggleExpandSelfAndChildren(expand?: boolean): void {
		this.isExpanded = expand || !this.isExpanded;
		this.childNodes.forEach(c => c.toggleExpandSelfAndChildren(this.isExpanded));
	}

	/* Recursively selects or unselects all child nodes */
	changeSelectStateOfChildren(select: boolean): void {
		this.childNodes.forEach(c => c.changeSelectStateOfSelfAndChildren(select));
		this.isRecentlyChecked = false;
		this.isRecentlyUnChecked = false;
	}

	changeSelectStateOfSelfAndChildren(select: boolean): void {
		select ? this.tree.selectNode(this) : this.tree.unselectNode(this);
		this.childNodes.forEach(c => c.changeSelectStateOfSelfAndChildren(select));
	}

	private initializeDom() {
		if (this.draggableHandler) {

			this.nodeTitleElement.setAttribute("draggable", "true");
			this.bindDragEvents(this.nodeTitleElement);
		}
	}

	async copyCodeToClipboard() {
		if (!this.current.code) {
			return;
		}

		try {
			await navigator.clipboard.writeText(this.current.code);
			this.notificationService.push("TreeNode_CopyCodeToClipboard", NotificationType.Success);
		} catch {
			this.notificationService.push("TreeNode_CopyCodeToClipboardFailed", NotificationType.Error);
		}
	}
}
